import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { getStampCard, updateStampCard } from "@/lib/api/admin";
import { parseCents } from "@/lib/utils/number";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/Spinner";

/** Digits and a single "." only, capped to 2 decimal places. */
function sanitizeMoneyInput(raw: string): string {
  return raw
    .replace(/[^\d.]/g, "")
    .replace(/(\..*)\./g, "$1")
    .replace(/(\.\d{2})\d+/, "$1");
}

/** Empty input means "no minimum" → 0 cents (what the API takes to clear it). */
function inputToCents(input: string): number {
  const trimmed = input.trim();
  return trimmed === "" ? 0 : parseCents(trimmed);
}

export function StampCardSettings({ storeId }: { storeId: string }) {
  const user = useAdminAuth();
  const qc = useQueryClient();

  // Pending edits of the minimum price / daily limit inputs; null = showing the server value.
  const [pending, setPending] = useState<string | null>(null);
  const [pendingLimit, setPendingLimit] = useState<string | null>(null);
  const [minError, setMinError] = useState<string | null>(null);
  const [limitError, setLimitError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    data: card,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["stamp-card", storeId],
    queryFn: () => getStampCard(user!, storeId),
    enabled: !!user,
  });

  const saveMutation = useMutation({
    mutationFn: (data: { enabled: boolean; stampMinPriceCents: number; stampDailyLimit: number }) =>
      updateStampCard(user!, storeId, data),
    onSuccess: (data) => {
      qc.setQueryData(["stamp-card", storeId], data);
      void qc.invalidateQueries({ queryKey: ["stamp-card", storeId] });
      setPending(null);
      setPendingLimit(null);
      setSaveError(null);
      toast.success("Stamp card updated.");
    },
    onError: (err) => setSaveError((err as Error).message),
  });

  if (isError)
    return (
      <Card>
        <CardContent className="py-4 text-center text-sm text-muted-foreground">
          Failed to load stamp card settings.
        </CardContent>
      </Card>
    );

  if (isLoading || card === undefined) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-4 border-primary" />
      </div>
    );
  }

  if (card === null) {
    return null;
  }

  const isPending = saveMutation.isPending;

  const serverMinCents = card.stamp_min_price_cents ?? 0;
  const serverInput = serverMinCents > 0 ? (serverMinCents / 100).toFixed(2) : "";
  const minPriceInput = pending ?? serverInput;
  const minCents = inputToCents(minPriceInput);

  // Empty daily limit = the platform default (sent as 0).
  const serverLimit = card.stamp_daily_limit ?? 0;
  const limitInput = pendingLimit ?? (serverLimit > 0 ? String(serverLimit) : "");
  const dailyLimit = limitInput === "" ? 0 : parseInt(limitInput, 10);

  // Compare cents, not strings, so "5" vs "5.00" isn't a change.
  const dirty =
    (pending !== null && minCents !== serverMinCents) ||
    (pendingLimit !== null && dailyLimit !== serverLimit);

  const handleSave = () => {
    const minErr =
      minPriceInput.trim() !== "" && minCents <= 0 ? "Enter a minimum price above $0." : null;
    const limitErr =
      limitInput !== "" && (dailyLimit < 1 || dailyLimit > 100)
        ? "Enter a daily limit from 1 to 100."
        : null;
    setMinError(minErr);
    setLimitError(limitErr);
    if (minErr || limitErr) return;
    setSaveError(null);
    // Keep the card's current enabled state — saving a price must not enable a disabled card.
    saveMutation.mutate({
      enabled: card.enabled,
      stampMinPriceCents: minCents,
      stampDailyLimit: dailyLimit,
    });
  };

  return (
    <Card className="overflow-hidden">
      <div className="border-b bg-muted/40 px-5 py-4">
        <h3 className="text-base font-semibold">Stamp Rewards</h3>
      </div>
      <div className="px-5 py-5">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Rule
          </p>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>Give</span>
            <span className="font-semibold text-primary">1 Stamp</span>
            <span>for every</span>
            <Input
              type="text"
              inputMode="decimal"
              value={minPriceInput}
              placeholder="0.00"
              startIcon="$"
              fullWidth={false}
              aria-label="Minimum price"
              aria-invalid={!!minError}
              onChange={(e) => {
                setPending(sanitizeMoneyInput(e.target.value));
                setMinError(null);
                setSaveError(null);
              }}
              className="w-24 bg-muted/40"
              disabled={isPending}
            />
            <span>spent.</span>
          </div>
          {minError && <p className="text-xs text-destructive">{minError}</p>}
          <p className="text-xs text-muted-foreground">
            Leave blank to reward every order, regardless of total.
          </p>
        </div>
        <div className="my-5 border-t" />
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Limit
          </p>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>Limit to</span>
            <Input
              type="text"
              inputMode="numeric"
              value={limitInput}
              placeholder={String(card.default_stamp_daily_limit)}
              maxLength={3}
              fullWidth={false}
              aria-label="Daily limit per customer"
              aria-invalid={!!limitError}
              onChange={(e) => {
                setPendingLimit(e.target.value.replace(/\D/g, ""));
                setLimitError(null);
                setSaveError(null);
              }}
              className="w-14 bg-muted/40 text-center"
              disabled={isPending}
            />
            <span>stamps per customer, per day.</span>
          </div>
          {limitError && <p className="text-xs text-destructive">{limitError}</p>}
          <p className="text-xs text-muted-foreground">
            Leave blank to use DISCA&apos;s default ({card.default_stamp_daily_limit}).
          </p>
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 border-t bg-muted/40 px-5 py-3">
        {saveError && <p className="mr-auto text-xs text-destructive">{saveError}</p>}
        <Button onClick={handleSave} disabled={!dirty || isPending}>
          {isPending ? <Spinner className="size-3.5 border-primary-foreground" /> : "Save Changes"}
        </Button>
      </div>
    </Card>
  );
}
